import { type CONVEYANCE_TYPES } from '@koala/sdk';
import { useEffect, useRef } from 'react';
import { useDispatch } from '@/redux';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { isWantedAtValidationError } from '@/utils/errors';
import { prepareErrorMessage } from '@/utils/global';

interface WantedAtRecovery {
  /** The failure from the menu read, if it failed. */
  error: unknown;
  /** The time that read carried — recovery is only possible when there is one to drop. */
  wantedAt: string | undefined;
  locationId: number | undefined;
  conveyanceType: CONVEYANCE_TYPES;
}

/**
 * TEAM-14594: a stored handoff time that has fallen outside the location's ordering horizon
 * makes the menu read fail with a `wanted-at` validation error, and keeps failing — the time
 * outlives the request, so every later read and every "Try again" re-sends it.
 *
 * Dropping the time is the only exit, so that is what this does. Clearing it changes the menu
 * query key, which makes React Query request the menu for now; there is no explicit refetch
 * here for that reason.
 *
 * Scoped deliberately narrowly: only a `wanted-at` rejection is recovered from, and only once
 * per time, so a genuine menu failure still reaches the error state with its retry intact.
 */
export function useWantedAtRecovery({
  error,
  wantedAt,
  locationId,
  conveyanceType,
}: WantedAtRecovery): void {
  const dispatch = useDispatch();
  /** The last time cleared, so a re-render cannot turn one rejection into a dispatch loop. */
  const clearedTime = useRef<string | null>(null);

  useEffect(() => {
    if (!error || !wantedAt || !locationId || clearedTime.current === wantedAt) {
      return;
    }

    let active = true;

    void (async () => {
      // The body has to be read to tell this rejection from any other 400, and reading it is
      // asynchronous, so the result is discarded if the component moved on in the meantime.
      const errorResponse = await prepareErrorMessage(null, error);

      if (!active || !isWantedAtValidationError(error, errorResponse)) {
        return;
      }

      clearedTime.current = wantedAt;
      dispatch(conveyanceModeActions.setWantedTime(undefined, locationId, conveyanceType));
    })();

    return () => {
      active = false;
    };
  }, [error, wantedAt, locationId, conveyanceType, dispatch]);
}
